import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type {
  ColumnSort,
  OnChangeFn,
  SortingState,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
} from '~/modules/artist-roster/components/Table';
import { TrackTableBody } from '~/modules/artist-roster/components/TrackList/TrackTableBody';

import { getArtistTracksPerformance } from '../../lib/tracks';
import type { ArtistDetails, AssetFamily } from '../../lib/types';
import { useTrackTableColumns } from './useTrackTableColumns';

export type TrackTableMeta = {
  artistName: string;
  filtersCount: number;
  filtersApplied: boolean;
  uaid: number;
  partyId: number;
};

interface TrackTableProps {
  data: AssetFamily[];
  meta: TrackTableMeta;
  artistData: ArtistDetails;
  isLoadingTracks: boolean;
  isFetchingTracks: boolean;
  onSortingChange?: OnChangeFn<SortingState>;
  columnSort?: ColumnSort;
}

export function TrackTable({
  data,
  meta,
  artistData,
  isLoadingTracks,
  isFetchingTracks,
  columnSort,
  onSortingChange,
}: TrackTableProps) {
  const { t } = useTranslation('roster');
  const familyIds = useMemo(
    () => [...new Set(data?.map((track) => track.id))].sort(),
    [data],
  );

  const { data: performancesData, isLoading: isLoadingPerformance } = useQuery({
    queryKey: ['artist-tracks-performance', familyIds],
    queryFn: () => getArtistTracksPerformance({ familyIds }),
    enabled: !isLoadingTracks && !!familyIds,
    placeholderData: keepPreviousData,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  const trackData = useMemo<AssetFamily[]>(() => {
    if (!performancesData) {
      return data;
    }

    return data.map((track) => {
      const performance = performancesData.get(track.id);
      if (!performance) {
        return track;
      }

      return {
        ...track,
        performance,
      };
    });
  }, [data, performancesData]);

  const columns = useTrackTableColumns({ range: 'TW' });
  const table = useReactTable({
    data: trackData,
    columns,
    meta,
    getCoreRowModel: getCoreRowModel(),
    enableSortingRemoval: false,
    manualSorting: true,
    onSortingChange,
    state: {
      sorting: columnSort ? [columnSort] : undefined,
    },
  });

  return (
    <Table
      aria-label={t('trackList.labels.aria')}
      className="group/table w-full text-left"
    >
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return (
                <TableHead
                  key={header.id}
                  className="px-sm text-body-sm font-normal first:w-1/3 first:pl-lg"
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TrackTableBody
        table={table}
        meta={meta}
        artistData={artistData}
        isFetchingTracks={isFetchingTracks}
        isLoadingPerformance={isLoadingPerformance}
      />
    </Table>
  );
}

TrackTable.displayName = 'TrackTable';
